import { EmptyStatePanel, ErrorState, LoadingState } from '@playforge/ui';
import { Badge, BlockStack, Button, Card, InlineStack, Page, Select, Text } from '@shopify/polaris';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import type { CampaignListItem, RewardView } from '../lib/types';

function rewardLabel(r: RewardView): string {
  if (r.label) return r.label;
  switch (r.type) {
    case 'percentage':
      return `${r.value ?? ''}% off`;
    case 'fixed':
      return `$${r.value ?? ''} off`;
    case 'free_shipping':
      return 'Free shipping';
    case 'free_product':
      return r.productTitle ? `Free ${r.productTitle}` : 'Free product';
    case 'store_credit':
      return `$${r.value ?? ''} store credit`;
    case 'no_reward':
      return 'No win';
    case 'try_again':
      return 'Try again';
    default:
      return r.type;
  }
}

function RewardList({ campaignId }: { campaignId: string }): JSX.Element {
  const navigate = useNavigate();
  const rewards = useApi(() => apiClient.get<RewardView[]>(`/rewards?campaignId=${campaignId}`), [campaignId]);

  if (rewards.loading) return <LoadingState label="Loading prizes" />;
  if (rewards.error) return <ErrorState message={rewards.error.message} onRetry={rewards.refetch} />;

  const list = rewards.data ?? [];
  const totalWeight = list.reduce((sum, r) => sum + (r.weight || 0), 0);

  if (list.length === 0) {
    return (
      <Card>
        <EmptyStatePanel
          heading="No prizes yet"
          primaryAction={{ content: 'Add prizes in builder', onAction: () => navigate(`/campaigns/${campaignId}`) }}
        >
          <p>Add at least one prize so customers have something to win.</p>
        </EmptyStatePanel>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            Prizes ({list.length})
          </Text>
          <Button onClick={() => navigate(`/campaigns/${campaignId}`)}>Edit prizes</Button>
        </InlineStack>
        <BlockStack gap="300">
          {list.map((r) => {
            const odds = totalWeight > 0 ? Math.round(((r.weight || 0) / totalWeight) * 100) : 0;
            return (
              <div key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 64px', gap: 12, alignItems: 'center' }}>
                <InlineStack gap="200" blockAlign="center">
                  <Text as="span" fontWeight="semibold">
                    {rewardLabel(r)}
                  </Text>
                  {r.remainingClaims != null ? (
                    <Badge {...(r.remainingClaims > 0 ? {} : { tone: 'critical' as const })}>
                      {`${r.remainingClaims} left`}
                    </Badge>
                  ) : null}
                </InlineStack>
                <div style={{ background: '#f1f1f4', borderRadius: 8, height: 22, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.max(2, odds)}%`, height: '100%', background: '#6366f1', borderRadius: 8 }} />
                </div>
                <Text as="span" alignment="end" variant="bodySm" fontWeight="semibold">
                  {odds}%
                </Text>
              </div>
            );
          })}
        </BlockStack>
        <Text as="p" tone="subdued" variant="bodySm">
          Win chance is each prize&apos;s weight relative to the total. Inventory-limited prizes stop being awarded once depleted.
        </Text>
      </BlockStack>
    </Card>
  );
}

export function RewardsPage(): JSX.Element {
  const navigate = useNavigate();
  const campaigns = useApi(() => apiClient.get<CampaignListItem[]>('/campaigns?limit=50'), []);
  const [campaignId, setCampaignId] = useState('');
  const selectedId = campaignId || campaigns.data?.[0]?.id || '';

  return (
    <Page title="Rewards" subtitle="The prizes customers can win, per campaign">
      {campaigns.loading ? <LoadingState label="Loading campaigns" /> : null}
      {campaigns.error ? <ErrorState message={campaigns.error.message} onRetry={campaigns.refetch} /> : null}

      {campaigns.data && campaigns.data.length === 0 ? (
        <Card>
          <EmptyStatePanel
            heading="Create a campaign first"
            primaryAction={{ content: 'Create campaign', onAction: () => navigate('/campaigns') }}
          >
            <p>Prizes belong to a campaign. Create one to start adding rewards.</p>
          </EmptyStatePanel>
        </Card>
      ) : null}

      {campaigns.data && campaigns.data.length > 0 ? (
        <BlockStack gap="400">
          <Card>
            <Select
              label="Campaign"
              options={campaigns.data.map((c) => ({ label: c.campaignName, value: c.id }))}
              value={selectedId}
              onChange={setCampaignId}
            />
          </Card>
          {selectedId ? <RewardList key={selectedId} campaignId={selectedId} /> : null}
        </BlockStack>
      ) : null}
    </Page>
  );
}
